export const CHAT_LIST_ROUTE_WATCH_KEY = '$route';

export const runChatListBeforeDestroy = ({
  abortConversationFetchController = null,
  abortSearchRequest = null,
  assigneeDebounceTimer = null,
  cleanupResizeObserver = null,
  clearPendingConversationTimers = null,
  clearScheduledTimer = null,
  clearScheduledTimers = null,
  removeEmitterListeners = null,
  removeRootListeners = null,
  routerAfterEachUnsubscribe = null,
  routerBeforeEachUnsubscribe = null,
  searchResultsRequestId = 0,
  setActiveConversationFetchKey = null,
  setAssigneeDebounceTimer = null,
  setIsComponentDestroyed = null,
  setSearchResultsRequestId = null,
  stopPeriodicUpdate = null,
} = {}) => {
  if (typeof setIsComponentDestroyed === 'function') {
    setIsComponentDestroyed(true);
  }
  if (typeof removeEmitterListeners === 'function') {
    removeEmitterListeners();
  }
  if (typeof stopPeriodicUpdate === 'function') {
    stopPeriodicUpdate();
  }
  if (typeof cleanupResizeObserver === 'function') {
    cleanupResizeObserver();
  }
  if (typeof removeRootListeners === 'function') {
    removeRootListeners();
  }
  if (typeof clearPendingConversationTimers === 'function') {
    clearPendingConversationTimers();
  }
  if (typeof clearScheduledTimers === 'function') {
    clearScheduledTimers();
  }

  const routerBeforeEachUnsubscribed =
    typeof routerBeforeEachUnsubscribe === 'function';
  if (routerBeforeEachUnsubscribed) {
    routerBeforeEachUnsubscribe();
  }

  const routerAfterEachUnsubscribed =
    typeof routerAfterEachUnsubscribe === 'function';
  if (routerAfterEachUnsubscribed) {
    routerAfterEachUnsubscribe();
  }

  const assigneeDebounceCleared = Boolean(
    assigneeDebounceTimer && typeof clearScheduledTimer === 'function'
  );
  if (assigneeDebounceCleared) {
    clearScheduledTimer(assigneeDebounceTimer);
    if (typeof setAssigneeDebounceTimer === 'function') {
      setAssigneeDebounceTimer(null);
    }
  }

  if (typeof abortConversationFetchController === 'function') {
    abortConversationFetchController();
  }
  if (typeof abortSearchRequest === 'function') {
    abortSearchRequest();
  }

  const nextSearchResultsRequestId = searchResultsRequestId + 1;
  if (typeof setSearchResultsRequestId === 'function') {
    setSearchResultsRequestId(nextSearchResultsRequestId);
  }
  if (typeof setActiveConversationFetchKey === 'function') {
    setActiveConversationFetchKey(null);
  }

  return {
    assigneeDebounceCleared,
    nextSearchResultsRequestId,
    routerAfterEachUnsubscribed,
    routerBeforeEachUnsubscribed,
    status: 'destroyed',
  };
};

export const runChatListMounted = ({
  addEmitterListeners = null,
  addRootListeners = null,
  getRouterContext = null,
  handleArchivedRouteAfterEach = null,
  handleArchivedRouteBeforeEach = null,
  initializeListView = null,
  initializeResizeObserver = null,
  setIsComponentDestroyed = null,
  setRouterAfterEachUnsubscribe = null,
  setRouterBeforeEachUnsubscribe = null,
  showConversationList = false,
} = {}) => {
  if (typeof setIsComponentDestroyed === 'function') {
    setIsComponentDestroyed(false);
  }

  const listViewInitialized =
    Boolean(showConversationList) && typeof initializeListView === 'function';
  if (listViewInitialized) {
    initializeListView();
  }

  const resizeObserverInitialized =
    typeof initializeResizeObserver === 'function';
  if (resizeObserverInitialized) {
    initializeResizeObserver();
  }

  const emitterListenersAdded = typeof addEmitterListeners === 'function';
  if (emitterListenersAdded) {
    addEmitterListeners();
  }

  const rootListenersAdded = typeof addRootListeners === 'function';
  if (rootListenersAdded) {
    addRootListeners();
  }

  const routerContext =
    typeof getRouterContext === 'function' ? getRouterContext() || {} : {};
  const routerGuardsRegistered =
    typeof routerContext.beforeEach === 'function' &&
    typeof routerContext.afterEach === 'function';

  if (routerGuardsRegistered) {
    const routerBeforeEachUnsubscribe = routerContext.beforeEach(
      handleArchivedRouteBeforeEach
    );
    const routerAfterEachUnsubscribe = routerContext.afterEach(
      handleArchivedRouteAfterEach
    );

    if (typeof setRouterBeforeEachUnsubscribe === 'function') {
      setRouterBeforeEachUnsubscribe(routerBeforeEachUnsubscribe);
    }
    if (typeof setRouterAfterEachUnsubscribe === 'function') {
      setRouterAfterEachUnsubscribe(routerAfterEachUnsubscribe);
    }
  }

  return {
    emitterListenersAdded,
    listViewInitialized,
    resizeObserverInitialized,
    rootListenersAdded,
    routerGuardsRegistered,
    status: 'mounted',
  };
};

export const buildChatListMountedContext = ({ component = null } = {}) => ({
  addEmitterListeners: component
    ? () => component.addEmitterListeners?.()
    : null,
  addRootListeners: component ? () => component.addRootListeners?.() : null,
  getRouterContext: component
    ? () => component.getRouterContext?.() || {}
    : null,
  handleArchivedRouteAfterEach: component?.handleArchivedRouteAfterEach || null,
  handleArchivedRouteBeforeEach:
    component?.handleArchivedRouteBeforeEach || null,
  initializeListView: component ? () => component.initializeListView?.() : null,
  initializeResizeObserver: component
    ? () => component.initializeResizeObserver?.()
    : null,
  setIsComponentDestroyed: component
    ? (isDestroyed) => {
        component.isComponentDestroyed = isDestroyed;
      }
    : null,
  setRouterAfterEachUnsubscribe: component
    ? (unsubscribe) => {
        component.routerAfterEachUnsubscribe = unsubscribe;
      }
    : null,
  setRouterBeforeEachUnsubscribe: component
    ? (unsubscribe) => {
        component.routerBeforeEachUnsubscribe = unsubscribe;
      }
    : null,
  showConversationList: Boolean(component?.showConversationList),
});

export const buildChatListBeforeDestroyContext = ({
  component = null,
} = {}) => ({
  abortConversationFetchController: component
    ? () => component.abortConversationFetchController?.()
    : null,
  abortSearchRequest: component ? () => component.abortSearchRequest?.() : null,
  assigneeDebounceTimer: component?.assigneeDebounceTimer || null,
  cleanupResizeObserver: component
    ? () => component.cleanupResizeObserver?.()
    : null,
  clearPendingConversationTimers: component
    ? () => component.clearPendingConversationTimers?.()
    : null,
  clearScheduledTimer: component
    ? (timer) => component.clearScheduledTimer?.(timer)
    : null,
  clearScheduledTimers: component
    ? () => component.clearScheduledTimers?.()
    : null,
  removeEmitterListeners: component
    ? () => component.removeEmitterListeners?.()
    : null,
  removeRootListeners: component
    ? () => component.removeRootListeners?.()
    : null,
  routerAfterEachUnsubscribe: component?.routerAfterEachUnsubscribe || null,
  routerBeforeEachUnsubscribe: component?.routerBeforeEachUnsubscribe || null,
  searchResultsRequestId: component?.searchResultsRequestId || 0,
  setActiveConversationFetchKey: component
    ? (fetchKey) => {
        component.activeConversationFetchKey = fetchKey;
      }
    : null,
  setAssigneeDebounceTimer: component
    ? (timer) => {
        component.assigneeDebounceTimer = timer;
      }
    : null,
  setIsComponentDestroyed: component
    ? (isDestroyed) => {
        component.isComponentDestroyed = isDestroyed;
      }
    : null,
  setSearchResultsRequestId: component
    ? (requestId) => {
        component.searchResultsRequestId = requestId;
      }
    : null,
  stopPeriodicUpdate: component ? () => component.stopPeriodicUpdate?.() : null,
});
